import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import type { getStarsReport } from '@generated/server/stars';

type Stars = Awaited<ReturnType<typeof getStarsReport>>;
const label = (d: string) => new Date(`${d}T12:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

/** Bar chart of ballots received each day since voting opened. */
export function StarsDaily({ r, forPdf = false }: { r: Stars; forPdf?: boolean }) {
  const days = r.daily;
  if (!days.length) return null;
  const best = days.reduce((a, b) => (b.count > a.count ? b : a));
  const dated = days.reduce((a, b) => a + b.count, 0);
  const last7 = days.slice(-7).reduce((a, b) => a + b.count, 0);
  return (
    <section className={forPdf ? "border-2 border-foreground bg-[hsl(var(--paper))] p-4" : "mt-6 border-2 border-foreground bg-[hsl(var(--paper))] p-5"}>
      <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-primary">Red Hook Stars · Ballots per day</div>
      <h3 className="font-[family-name:var(--font-heading)] text-2xl font-semibold">Every day since voting opened</h3>
      <div className="mt-3 flex flex-wrap gap-x-8 gap-y-2 text-sm">
        <div><span className="font-[family-name:var(--font-heading)] text-3xl tabular-nums">{days.length}</span> days</div>
        <div><span className="font-[family-name:var(--font-heading)] text-3xl tabular-nums">{last7}</span> ballots in the last 7 days</div>
        <div>Busiest day: <strong>{label(best.day)}</strong> · {best.count} ballots</div>
      </div>
      <div className={forPdf ? 'mt-4' : 'mt-4 h-72 w-full min-w-0'}>
        {forPdf ? (
          <BarChart width={500} height={240} data={days} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
            <CartesianGrid vertical={false} stroke="hsl(var(--border))" />
            <XAxis dataKey="day" tickFormatter={label} tick={{ fontSize: 11 }} interval="preserveStartEnd" minTickGap={24} />
            <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
            <Bar dataKey="count" fill="hsl(var(--primary))" isAnimationActive={false} />
          </BarChart>
        ) : (
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={days} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
            <CartesianGrid vertical={false} stroke="hsl(var(--border))" />
            <XAxis dataKey="day" tickFormatter={label} tick={{ fontSize: 11 }} interval="preserveStartEnd" minTickGap={24} />
            <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
            <Tooltip labelFormatter={(d) => label(String(d))} formatter={(v) => [`${v} ballots`, 'Received']} cursor={{ fill: 'hsl(var(--muted))' }} />
            <Bar dataKey="count" fill="hsl(var(--primary))" radius={[2, 2, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
        )}
      </div>
      <p className="mt-2 text-xs text-muted-foreground">{dated} dated ballots{r.undated ? ` · ${r.undated} ballots typed into the sheet without a date aren't shown on the chart` : ''}. Updates live from the ballot sheet.</p>
    </section>
  );
}
